import { Component } from '@theme/component';

/**
 * Contact and studios (sections/contact-studios.liquid): the live studio sentences.
 *
 * "It's <time> <part of day> in <city>, <temp> and <weather>." Time ticks every second from the
 * visitor's clock in the studio's zone; open means Monday to Friday, 9:00 to 18:00 local.
 * Weather comes from open-meteo (no key), fetched once the page is idle and refreshed every
 * 15 minutes. If it fails, the weather clause is left out.
 *
 * @typedef {{ studios?: HTMLElement[], sentences?: HTMLElement[], days?: HTMLElement[], statuses?: HTMLElement[] }} StudioRefs
 * @extends {Component<StudioRefs>}
 */
class ContactStudiosComponent extends Component {
  /** @type {number | undefined} */
  #clock;

  /** @type {number | undefined} */
  #weatherTimer;

  /** @type {Record<string, { temperature_2m: number, weather_code: number, is_day: number }>} */
  #weather = {};

  connectedCallback() {
    super.connectedCallback();
    if (!this.refs.studios?.length) return;
    this.#render();
    this.#clock = window.setInterval(() => this.#render(), 1000);
    const idle = window.requestIdleCallback ?? ((/** @type {() => void} */ cb) => window.setTimeout(cb, 1500));
    idle(() => this.#loadWeather());
    this.#weatherTimer = window.setInterval(() => this.#loadWeather(), 15 * 60 * 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#clock);
    window.clearInterval(this.#weatherTimer);
  }

  async #loadWeather() {
    const studios = (this.refs.studios ?? []).filter((s) => s.dataset.lat && s.dataset.lon);
    if (!studios.length) return;
    const lat = studios.map((s) => s.dataset.lat).join(',');
    const lon = studios.map((s) => s.dataset.lon).join(',');
    try {
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code,is_day`
      );
      if (!response.ok) return;
      /** @type {any} */
      let json = await response.json();
      if (!Array.isArray(json)) json = [json];
      studios.forEach((studio, i) => {
        const current = json[i]?.current;
        if (current && studio.dataset.city) this.#weather[studio.dataset.city] = current;
      });
      this.#render();
    } catch {
      // Weather is decoration; leave it out.
    }
  }

  /**
   * @param {number} code
   * @param {boolean} day
   */
  #describe(code, day) {
    /** @type {[number[], string, string][]} */
    const table = [
      [[0], 'clear', day ? '☀️' : '🌙'],
      [[1], 'mostly clear', day ? '🌤️' : '🌙'],
      [[2], 'partly cloudy', '⛅'],
      [[3], 'cloudy', '☁️'],
      [[45, 48], 'foggy', '🌫️'],
      [[51, 53, 55, 56, 57], 'drizzly', '🌦️'],
      [[61, 63, 65, 66, 67, 80, 81, 82], 'raining', '🌧️'],
      [[71, 73, 75, 77, 85, 86], 'snowing', '🌨️'],
      [[95, 96, 99], 'stormy', '⛈️'],
    ];
    // Words come from the locale (comma-separated, in the same order as the table, then the fallback).
    const words = (this.dataset.weatherWords ?? '').split(',').map((w) => w.trim());
    const index = table.findIndex(([codes]) => codes.includes(code));
    const match = table[index];
    return match
      ? { word: words[index] || match[1], icon: match[2] }
      : { word: words[table.length] || 'mild', icon: '🌡️' };
  }

  #render() {
    const labels = this.dataset;
    const now = new Date();
    (this.refs.studios ?? []).forEach((studio, i) => {
      const zone = studio.dataset.zone;
      const city = studio.dataset.city ?? '';
      if (!zone) return;
      /** @type {Record<string, string>} */
      const parts = {};
      try {
        for (const part of new Intl.DateTimeFormat('en-GB', {
          timeZone: zone,
          hour: '2-digit',
          minute: '2-digit',
          weekday: 'long',
          hour12: false,
          timeZoneName: 'short',
        }).formatToParts(now)) {
          parts[part.type] = part.value;
        }
      } catch {
        return;
      }

      const hour = Number(parts.hour) % 24;
      const weekend = parts.weekday === 'Saturday' || parts.weekday === 'Sunday';
      const open = !weekend && hour >= 9 && hour < 18;
      const partOfDay =
        hour < 5
          ? labels.labelNight
          : hour < 12
            ? labels.labelMorning
            : hour < 17
              ? labels.labelAfternoon
              : hour < 21
                ? labels.labelEvening
                : labels.labelNight;
      const h12 = `${hour % 12 || 12}:${parts.minute}`;

      const sentence = this.refs.sentences?.[i];
      if (sentence) {
        const weather = this.#weather[city];
        const time = document.createElement('span');
        time.className = 'contact-studios__time';
        time.textContent = h12;
        let weatherText = '';
        if (weather) {
          const { word, icon } = this.#describe(weather.weather_code, Boolean(weather.is_day));
          weatherText = (labels.weather ?? '')
            .replace('[temp]', String(Math.round(weather.temperature_2m)))
            .replace('[weather]', `${word} ${icon}`);
        }
        // Locale template, e.g. "It’s [time] [part] in [city][weather]."
        const [before = '', after = ''] = (labels.sentence ?? '[time]').split('[time]');
        const fill = (/** @type {string} */ text) =>
          text.replace('[part]', partOfDay ?? '').replace('[city]', city).replace('[weather]', weatherText);
        sentence.replaceChildren(fill(before), time, fill(after));
      }

      const day = this.refs.days?.[i];
      if (day) day.textContent = `${parts.weekday} · ${parts.timeZoneName}`;

      const status = this.refs.statuses?.[i];
      if (status) {
        status.hidden = false;
        status.textContent = open ? labels.labelOpen ?? '' : labels.labelClosed ?? '';
        status.toggleAttribute('data-open', open);
      }
    });
  }
}

if (!customElements.get('contact-studios-component')) {
  customElements.define('contact-studios-component', ContactStudiosComponent);
}
